import { getProductsProductIdUpsell } from '@agos/api-client/menu'

import { addonsOf, type Addon } from './addons'
import { tt } from './i18n/translation-memory'
import { formatCents } from './money'

/**
 * *Chcete si priobjednať niečo navyše?* with groups, their limits and their prices - `FRONTEND.md` §9.712, on
 * `BACKEND.md` §10A.365's public read (owner O63, O69, answers #133, #134, #136).
 *
 * **The groups are a layer over the links that already exist**, not a second kind of add-on. An item in a group is
 * the same ordinary product `addons.ts` describes - its own id and VAT - and what the group adds is two facts about
 * *this offer*: how many may be chosen per unit of the product (`maxPerUnit`, null for no limit) and what each costs
 * when chosen here (`priceCents`, null for the item's own price, 0 for free). The server re-prices a grouped line and
 * enforces the limit (§10A.366), so both are an honest quote rather than an instruction.
 *
 * **Here rather than in `@agos/ui`**, because the rules are pure and two apps' call sites need them as well as the
 * component: the prompt's state is opened from the customer menu, the customer basket and the till, and §9.657
 * already moved `addons.ts` here for the same reason.
 */

export interface UpsellGroup {
  id: number
  name: string
  /** How many of this group's items one unit of the product allows. Null is no limit. */
  maxPerUnit: number | null
  /** What each item costs when chosen in this group, in cents. Null is each item at its own price; 0 is free. */
  priceCents: number | null
  items: Addon[]
}

export interface UpsellOffer {
  /** Active groups in their order, each with its items in item order. */
  groups: UpsellGroup[]
  /** The ungrouped add-ons, offered exactly as before §9.712 - under their menu category. */
  items: Addon[]
}

/**
 * One item somebody chose, and the terms it was chosen under - what the prompt's confirm hands back.
 *
 * `upsellGroupId` is null for an ungrouped add-on. `unitPriceCents` is the group's price when it sets one, the item's
 * own otherwise: the figure the basket quotes, which the server re-derives from the group rather than taking.
 */
export interface UpsellChoice {
  product: Addon
  upsellGroupId: number | null
  unitPriceCents: number
  /**
   * The group's own terms, carried so the basket can hold them on the line (§9.713): `groupPriceCents` is the
   * group's `priceCents` (null is *each item at its own price*), `maxPerUnit` its limit per unit of the parent (null is
   * no limit). Both null for an ungrouped add-on.
   */
  groupPriceCents: number | null
  maxPerUnit: number | null
}

export const NO_UPSELL: UpsellOffer = { groups: [], items: [] }

/**
 * `GET /products/{productId}/upsell`, read defensively - §10A.365.
 *
 * **Every item goes through `addonsOf`**, so a withdrawn or switched-off product is not offered in a group either:
 * §9.232's rule (*suggesting what cannot be bought is worse than not suggesting it*) has nothing to do with whether
 * the link carries a group.
 *
 * **A group whose terms cannot be read is left out rather than offered on guessed terms.** A limit read as *no
 * limit* lets somebody choose what the server will refuse at the end (§9.155's late refusal), and a price read as the
 * item's own quotes a free item at its full price. A group with no item left after the filter is left out too: a
 * heading over nothing is a question with no answers.
 *
 * **An item is offered once.** One pair is one link on the server (§10A.364's unique index), so the same add-on in
 * two places is a payload this client did not expect; the first place wins rather than drawing it twice, which would
 * let one dressing be chosen twice on two sets of terms.
 */
export function upsellOf(payload: unknown): UpsellOffer {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return NO_UPSELL
  const row = payload as Record<string, unknown>
  const seen = new Set<number>()
  const once = (items: Addon[]) =>
    items.filter((item) => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })
  const groups: UpsellGroup[] = []
  for (const raw of Array.isArray(row.groups) ? row.groups : []) {
    const group = groupOf(raw)
    if (group === null) continue
    const items = once(group.items)
    if (items.length > 0) groups.push({ ...group, items })
  }
  return { groups, items: once(addonsOf(row.items)) }
}

function groupOf(raw: unknown): UpsellGroup | null {
  if (typeof raw !== 'object' || raw === null) return null
  const row = raw as Record<string, unknown>
  if (typeof row.id !== 'number' || !Number.isInteger(row.id)) return null
  const name = typeof row.name === 'string' ? row.name.trim() : ''
  if (name === '') return null
  // The public read sends active groups only; a payload that names an inactive one is not offered either.
  if (row.active === false) return null
  const maxPerUnit = termOf(row.maxPerUnit, 1)
  const priceCents = termOf(row.priceCents, 0)
  if (maxPerUnit === undefined || priceCents === undefined) return null
  return { id: row.id, name, maxPerUnit, priceCents, items: addonsOf(row.items) }
}

/** Null or absent is *not set*; an integer at or above `floor` is the value; anything else cannot be read. */
function termOf(value: unknown, floor: number): number | null | undefined {
  if (value === null || value === undefined) return null
  if (typeof value === 'number' && Number.isInteger(value) && value >= floor) return value
  return undefined
}

/** Whether there is anything to ask about. A product with no upsells never opens the prompt. */
export function upsellIsEmpty(offer: UpsellOffer): boolean {
  return offer.groups.length === 0 && offer.items.length === 0
}

/**
 * How many units of the product the prompt is for - a positive whole number.
 *
 * **Every call site opens it for the one unit just added** (#136, *every added unit*). It is more than one only when a
 * second unit was added before the first question arrived: two taps on *+* while the read is in flight are asked
 * once, for both, rather than the second answer silently replacing the first (`upsellPromptAfter`).
 */
export function upsellUnits(units: number): number {
  return Number.isFinite(units) && units >= 1 ? Math.floor(units) : 1
}

/**
 * A group's limit for this prompt - `maxPerUnit` × the units it was opened for, or null for no limit.
 *
 * **Per unit, by the owner's answer #133**: *two garlic sticks allow two dips*. The server checks the same product
 * against the parent's quantity (§10A.366), and every prompt is opened for the units it was added with, so the sum of
 * what the prompts allowed never exceeds what the server will accept.
 */
export function upsellGroupLimit(group: Pick<UpsellGroup, 'maxPerUnit'>, units: number): number | null {
  return group.maxPerUnit === null ? null : group.maxPerUnit * upsellUnits(units)
}

/** One of, drawn as radio buttons - a limit of exactly one for this prompt (one per unit, for one unit). */
export function upsellGroupIsRadio(group: Pick<UpsellGroup, 'maxPerUnit'>, units: number): boolean {
  return upsellGroupLimit(group, units) === 1
}

/** What one item costs in this group: the group's price where it sets one, the item's own otherwise. */
export function upsellUnitCents(group: Pick<UpsellGroup, 'priceCents'> | null, addon: Pick<Addon, 'priceCents'>): number {
  return group === null || group.priceCents === null ? addon.priceCents : group.priceCents
}

/** *zadarmo* at nought, the money otherwise - the words on a free item and on its group's heading. */
export function upsellPriceLabel(cents: number): string {
  return cents === 0 ? tt('zadarmo') : formatCents(cents)
}

/** *vyberte najviac N* for a limited group, nothing for an unlimited one. */
export function upsellLimitLabel(group: Pick<UpsellGroup, 'maxPerUnit'>, units: number): string | null {
  const limit = upsellGroupLimit(group, units)
  return limit === null ? null : `${tt('vyberte najviac')} ${String(limit)}`
}

/**
 * What has been ticked. A key names the group as well as the item, so an item's terms travel with the tick.
 *
 * A set of strings rather than a map of sets: it is what React holds in state, and every rule below is a count or a
 * membership test over it.
 */
export type UpsellSelection = ReadonlySet<string>

export function upsellKey(groupId: number | null, addonId: number): string {
  return groupId === null ? `item:${String(addonId)}` : `group:${String(groupId)}:${String(addonId)}`
}

/** How many of a group's items are ticked. */
export function upsellChosenIn(selection: UpsellSelection, groupId: number): number {
  const prefix = `group:${String(groupId)}:`
  let count = 0
  for (const key of selection) if (key.startsWith(prefix)) count += 1
  return count
}

/** Whether a group has reached its limit for this prompt, so its unticked items are offered no more. */
export function upsellGroupIsFull(
  selection: UpsellSelection,
  group: Pick<UpsellGroup, 'id' | 'maxPerUnit'>,
  units: number,
): boolean {
  const limit = upsellGroupLimit(group, units)
  return limit !== null && upsellChosenIn(selection, group.id) >= limit
}

/**
 * One press on an item.
 *
 * Ticked, it comes off. Unticked: in a one-of group it **replaces** the group's other choice, which is a radio
 * button's whole behaviour; in a group already at its limit it is refused and the selection is returned unchanged; an
 * ungrouped item has no limit, as before §9.712. An item the offer does not hold is refused too, so a stale handler
 * cannot tick something the confirm would then hand back without terms.
 */
export function toggleUpsell(
  offer: UpsellOffer,
  selection: UpsellSelection,
  groupId: number | null,
  addonId: number,
  units: number,
): UpsellSelection {
  const key = upsellKey(groupId, addonId)
  if (selection.has(key)) {
    const next = new Set(selection)
    next.delete(key)
    return next
  }
  if (groupId === null) {
    if (!offer.items.some((item) => item.id === addonId)) return selection
    return new Set([...selection, key])
  }
  const group = offer.groups.find((candidate) => candidate.id === groupId)
  if (group === undefined || !group.items.some((item) => item.id === addonId)) return selection
  if (upsellGroupIsRadio(group, units)) {
    const prefix = `group:${String(groupId)}:`
    return new Set([...[...selection].filter((chosen) => !chosen.startsWith(prefix)), key])
  }
  if (upsellGroupIsFull(selection, group, units)) return selection
  return new Set([...selection, key])
}

/**
 * The confirm's answer - one entry per ticked item, in the order the prompt draws them: the groups first, then the
 * ungrouped add-ons.
 */
export function upsellChoices(offer: UpsellOffer, selection: UpsellSelection): UpsellChoice[] {
  const choices: UpsellChoice[] = []
  for (const group of offer.groups) {
    for (const item of group.items) {
      if (selection.has(upsellKey(group.id, item.id))) {
        choices.push({
          product: item,
          upsellGroupId: group.id,
          unitPriceCents: upsellUnitCents(group, item),
          groupPriceCents: group.priceCents,
          maxPerUnit: group.maxPerUnit,
        })
      }
    }
  }
  for (const item of offer.items) {
    if (selection.has(upsellKey(null, item.id))) {
      choices.push({
        product: item,
        upsellGroupId: null,
        unitPriceCents: item.priceCents,
        groupPriceCents: null,
        maxPerUnit: null,
      })
    }
  }
  return choices
}

/** What the choices come to, in cents - shown before anybody commits to them. */
export function upsellChoicesTotalCents(choices: readonly UpsellChoice[]): number {
  return choices.reduce((total, choice) => total + choice.unitPriceCents, 0)
}

/**
 * The prompt a call site is holding open: which product, for how many units, and on behalf of which line.
 *
 * `parentKey` is the caller's own name for the line the units were added to - the customer basket's `lineKey`, the
 * till's product-and-size. It is what tells *a second unit of the same line* (asked together) from *a different
 * line* (asked afresh), and it is what a confirm handler reads to say which line the chosen items were ordered with.
 */
export interface UpsellPrompt {
  productId: number
  productName: string
  parentKey: string
  offer: UpsellOffer
  units: number
}

/**
 * The prompt to hold after another unit's offer arrives.
 *
 * **Asked for every unit (#136), and asked once for two units that arrived together.** A question already open - or
 * still on its way - for the same line takes the new unit into its limits rather than being replaced: replacing it
 * would drop the first unit's question and keep only the last one's, which is what a double tap on *+* did. A
 * different line's offer replaces it, as before.
 */
export function upsellPromptAfter(current: UpsellPrompt | null, next: UpsellPrompt): UpsellPrompt {
  if (current !== null && current.productId === next.productId && current.parentKey === next.parentKey) {
    return { ...current, units: upsellUnits(current.units) + upsellUnits(next.units) }
  }
  return { ...next, units: upsellUnits(next.units) }
}

/**
 * Whether a basket line's *+* asks the question - #136, *both baskets' "+"*.
 *
 * **A catalogue product only, which is the scope the menu's own add buttons have always had** (`addPlain`, the till's
 * tile): a gift is not bought, a box slot and a day's menu meal are priced as part of their set, a composed pizza has
 * no product to read upsells for (its id is a hash of its name, negative by construction), and a line that is itself
 * an upsell was the answer to a question rather than a reason to ask one - asking *anything with your dip?* after
 * every extra dip is the prompt interrupting its own answer.
 *
 * **Structural, so both baskets' lines satisfy it and a field added later is honoured without an edit here**:
 * `addonOf` is §9.713's parent link (lane U4), which the type names before either basket carries it.
 */
export function asksUpsellFor(line: {
  productId: number
  giftRuleId?: unknown
  boxGroupId?: unknown
  lunchMealType?: unknown
  lunchGroup?: unknown
  composed?: unknown
  parts?: unknown
  upsellGroupId?: unknown
  addonOf?: unknown
}): boolean {
  return (
    Number.isInteger(line.productId) &&
    line.productId > 0 &&
    line.giftRuleId === undefined &&
    line.boxGroupId === undefined &&
    line.lunchMealType === undefined &&
    line.lunchGroup !== true &&
    line.composed === undefined &&
    line.parts === undefined &&
    line.upsellGroupId === undefined &&
    line.addonOf === undefined
  )
}

/**
 * `GET /api/v1/products/{productId}/upsell` - no login, like the `/addons` read it extends.
 *
 * **Fired after the add, never before**, and its failure is the caller's to swallow: a product already in the basket
 * must not be undone by a lookup (`addons.ts`).
 */
export async function fetchUpsell(productId: number): Promise<UpsellOffer> {
  // The generated call since §10A.365 was regenerated into the client; the answer still goes through `upsellOf`, which
  // drops what cannot be offered (a withdrawn item, a group whose terms cannot be read).
  return upsellOf(await getProductsProductIdUpsell(productId))
}

/**
 * Ask about a unit that is **already** in the basket - §9.712, #136. One copy for the customer menu, the customer
 * basket and the till, so *add first, ask second*, *a product with no upsells opens nothing* and *a failed lookup
 * undoes nothing* are decided once rather than at every *+*.
 *
 * `hold` is the caller's state setter: it receives an update, so a second unit of the same line arriving while the
 * first question is open (or still on its way) joins it rather than replacing it (`upsellPromptAfter`). The returned
 * promise never rejects; a caller fires it with `void` and never awaits it before adding.
 */
export async function offerUpsell(
  ask: { productId: number; productName: string; parentKey: string },
  hold: (update: (current: UpsellPrompt | null) => UpsellPrompt | null) => void,
): Promise<void> {
  let offer: UpsellOffer
  try {
    offer = await fetchUpsell(ask.productId)
  } catch {
    return
  }
  if (upsellIsEmpty(offer)) return
  hold((current) => upsellPromptAfter(current, { ...ask, offer, units: 1 }))
}
